// // frontend/app/components/SupportCenterModal.js
// // # BDD 01/21/2026 - 01/22/2026 - add_subscription_notifications_and_support
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getVersionInfo, formatClientVersion } from "../utils/versionInfo";

import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import axios from "axios";
import { GLOSSARY_KINDS, getGlossaryEntry, getGlossaryAccentColor } from "../i18n/glossary";

function apiPath(path) {
  const p = String(path || "").replace(/^\/+/, "");
  const base = String(axios?.defaults?.baseURL || "");
  if (base.includes("/api")) return p;
  return `api/${p}`;
}

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return String(iso);
  }
};

const norm = (s) => String(s || "").trim().toLowerCase();
const parseDateOnly = (value) => {
  if (!value) return null;
  try {
    const raw = String(value).slice(0, 10);
    const [y, m, d] = raw.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  } catch {
    return null;
  }
};
const isoDate = (value) => String(value || "").slice(0, 10);

const STATUS_ORDER = ["open", "pending", "resolved", "closed"];
const statusLabel = (s) => {
  const v = norm(s);
  if (!v) return "open";
  return v;
};

export default function SupportCenterModal({
  visible,
  onClose,
  user,
  isDarkMode,
  t,
  activeModule,
  currentCurriculum,
  contentDb,
}) {
  const styles = useMemo(() => makeStyles(isDarkMode), [isDarkMode]);
  const isInternalAdmin = !!user?.is_internal_admin;
  const isSchoolAdmin = norm(user?.role) === "schooladmin";
  const [clientVersion, setClientVersion] = useState(null);

  // tabs: "new" | "tickets" | "detail"
  const [tab, setTab] = useState("tickets");

  // Create ticket
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("general");
  const [priority, setPriority] = useState("normal");
  const [message, setMessage] = useState("");
  const [createBusy, setCreateBusy] = useState(false);

  // Tickets list
  const [ticketsBusy, setTicketsBusy] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketsErr, setTicketsErr] = useState("");

  // Ticket detail
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyInternal, setReplyInternal] = useState(false);

  // Internal admin controls
  const [assignTo, setAssignTo] = useState("");

  // Optional: show subscription snippet (no new backend required; uses /api/subscription/status if present)
  const [subLine, setSubLine] = useState("");

  const scrollRef = useRef(null);

  useEffect(() => {
    if (!visible) return;

    setTicketsErr("");
    setReplyInternal(false);
    setAssignTo("");

    setTab("tickets");
    fetchTickets();

    // best-effort subscription status line (helps support context)
    fetchSubscriptionLine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // load client version from /version.json (best-effort)
    getVersionInfo()
    .then((info) => setClientVersion(formatClientVersion(info)))
    .catch(() => setClientVersion(null));

  }, [visible]);

  const fetchSubscriptionLine = async () => {
    try {
      // If you have /api/subscription/status, use it; else fallback to user fields
      const resp = await axios.get(apiPath("subscription/status"));
      const st = resp?.data || {};
      const end = st?.user_end_date || st?.effective_end_date || user?.access_end_date || null;
      const endDate = parseDateOnly(end);
      const graceDays = Number(st?.grace_days ?? 14);
      const cutoffDate = (() => {
        if (st?.user_end_date) {
          const d = parseDateOnly(st.user_end_date);
          if (d) {
            d.setDate(d.getDate() + graceDays);
            return d;
          }
        }
        return parseDateOnly(st?.effective_cutoff_date || user?.access_cutoff_date || null);
      })();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const status = (() => {
        const userStatus = norm(st?.user_access_status || "");
        if (userStatus === "active" || userStatus === "expired" || userStatus === "in_grace" || userStatus === "expiring_soon") {
          return userStatus;
        }
        if (!endDate) return norm(st?.status || "unknown");
        if (today <= endDate) return "active";
        if (cutoffDate && today <= cutoffDate) return "in_grace";
        return "expired";
      })();
      const cutoff = cutoffDate ? isoDate(cutoffDate.toISOString()) : "";
      if (endDate) {
        const endLabel = isoDate(endDate.toISOString());
        setSubLine(`Subscription: ${status.toUpperCase()} • ends ${endLabel}${cutoff ? ` • cutoff ${cutoff}` : ""}`);
        return;
      }
      if (end) {
        setSubLine(`Subscription: ${status.toUpperCase()} • ends ${String(end).slice(0, 10)} • cutoff ${String(cutoff || "").slice(0, 10)}`);
      } else {
        setSubLine("Subscription: (not available)");
      }
    } catch {
      const end = user?.access_end_date;
      if (end) setSubLine(`Subscription: ends ${String(end).slice(0, 10)}`);
      else setSubLine("Subscription: (not available)");
    }
  };

  const fetchTickets = async () => {
    try {
      setTicketsBusy(true);
      setTicketsErr("");
      const resp = await axios.get(apiPath("support/tickets"));
      const list = resp?.data?.tickets || [];
      setTickets(list);
    } catch (e) {
      setTicketsErr(e?.response?.data?.detail || String(e));
    } finally {
      setTicketsBusy(false);
    }
  };

  const openTicket = async (ticket) => {
    if (!ticket?.id) return;
    setSelectedTicket(ticket);
    setAssignTo(ticket?.assigned_to_email || "");
    setTab("detail");
    await fetchTicketDetail(ticket.id);
  };

  const fetchTicketDetail = async (ticketId) => {
    try {
      setDetailBusy(true);
      const resp = await axios.get(apiPath(`support/tickets/${ticketId}`));
      const tk = resp?.data?.ticket || selectedTicket;
      setSelectedTicket(tk);
      setAssignTo(tk?.assigned_to_email || "");
      setMessages(resp?.data?.messages || []);
      setTimeout(() => scrollRef.current?.scrollToEnd?.({ animated: true }), 250);
    } catch (e) {
      setTicketsErr(e?.response?.data?.detail || String(e));
    } finally {
      setDetailBusy(false);
    }
  };

  const submitTicket = async () => {
    const sub = subject.trim();
    const msg = message.trim();
    if (!sub || !msg) {
      setTicketsErr("Please enter a subject and message.");
      return;
    }

    // Ensure client_version is available (avoid async timing race)
    let cv = clientVersion;
    if (!cv) {
    try {
        const info = await getVersionInfo();
        cv = formatClientVersion(info);
        if (cv) setClientVersion(cv);
    } catch (_) {
        cv = null;
    }
    }

    try {
      setCreateBusy(true);
      setTicketsErr("");

      const ua =
        typeof navigator !== "undefined" && navigator?.userAgent ? navigator.userAgent : null;

      const payload = {
        subject: sub,
        category: (category || "general").trim(),
        priority: (priority || "normal").trim(),
        message: msg,
        metadata: {
          client_platform: Platform.OS,
          client_user_agent: ua, // web only (future use)
          client_version: cv,  // keep empty for now; fill later
          server_version: null,  // server will overwrite/fill
          content_db: user?.content_db || contentDb || null,
          school_id: user?.school_id ?? null,
          school: user?.school ?? null,
          role: user?.role ?? null,
          active_module: activeModule || null,
          current_curriculum: currentCurriculum || null,
        },
      };

      const resp = await axios.post(apiPath("support/tickets"), payload);
      const newId = resp?.data?.ticket_id;

      // Clear form
      setSubject("");
      setMessage("");
      setCategory("general");
      setPriority("normal");

      await fetchTickets();
      if (newId) {
        await openTicket({ id: newId });
      } else {
        setTab("tickets");
      }
    } catch (e) {
      setTicketsErr(e?.response?.data?.detail || String(e));
    } finally {
      setCreateBusy(false);
    }
  };

  const sendReply = async () => {
    const txt = replyText.trim();
    if (!txt || !selectedTicket?.id) return;

    try {
      setReplyBusy(true);
      setTicketsErr("");

      await axios.post(apiPath(`support/tickets/${selectedTicket.id}/messages`), {
        message: txt,
        is_internal: !!replyInternal,
      });

      setReplyText("");
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
    } catch (e) {
      setTicketsErr(e?.response?.data?.detail || String(e));
    } finally {
      setReplyBusy(false);
    }
  };

  const canReopen = useMemo(() => {
    const s = statusLabel(selectedTicket?.status);
    if (!(s === "resolved" || s === "closed")) return false;
    if (isInternalAdmin) return true;
    return norm(selectedTicket?.created_by_email) === norm(user?.email);
  }, [selectedTicket, user, isInternalAdmin]);

  const reopenTicket = async () => {
    if (!selectedTicket?.id) return;
    try {
      setTicketsErr("");
      setDetailBusy(true);
      await axios.post(apiPath(`support/tickets/${selectedTicket.id}/reopen`), {});
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
    } catch (e) {
      setTicketsErr(e?.response?.data?.detail || String(e));
    } finally {
      setDetailBusy(false);
    }
  };

  const updateStatus = async (newStatus) => {
    if (!selectedTicket?.id) return;
    try {
      setTicketsErr("");
      setDetailBusy(true);
      await axios.patch(apiPath(`support/tickets/${selectedTicket.id}`), { status: newStatus });
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
    } catch (e) {
      setTicketsErr(e?.response?.data?.detail || String(e));
    } finally {
      setDetailBusy(false);
    }
  };

  const updateAssignment = async () => {
    if (!selectedTicket?.id) return;
    try {
      setTicketsErr("");
      setDetailBusy(true);
      await axios.patch(apiPath(`support/tickets/${selectedTicket.id}`), {
        assigned_to_email: assignTo?.trim() || null,
      });
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
    } catch (e) {
      setTicketsErr(e?.response?.data?.detail || String(e));
    } finally {
      setDetailBusy(false);
    }
  };

  const closeAll = () => onClose?.();

  const renderError = () =>
    ticketsErr ? (
      <View style={styles.errorBox}>
        <Text style={styles.errorText}>{ticketsErr}</Text>
      </View>
    ) : null;

  const renderTopBar = () => (
    <View style={styles.topBar}>
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <MaterialCommunityIcons name="lifebuoy" size={20} color={styles._c.text} />
        <Text style={styles.topTitle}>{t?.("Support") || "Support"}</Text>
      </View>

      <View style={{ flexDirection: "row", gap: 10, alignItems: "center" }}>
        <TouchableOpacity onPress={() => setTab("new")} style={[styles.tabBtn, tab === "new" && styles.tabBtnActive]}>
          <Text style={[styles.tabText, tab === "new" && styles.tabTextActive]}>
            {t?.("New Ticket") || "New Ticket"}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => {
            setTab("tickets");
            fetchTickets();
          }}
          style={[styles.tabBtn, tab === "tickets" && styles.tabBtnActive]}
        >
          <Text style={[styles.tabText, tab === "tickets" && styles.tabTextActive]}>
            {isInternalAdmin ? (t?.("Tickets") || "Tickets") : (t?.("My Tickets") || "My Tickets")}
          </Text>
        </TouchableOpacity>

        {/* AIT-251: glossary tab — "What's the difference?" */}
        <TouchableOpacity
          onPress={() => setTab("glossary")}
          style={[styles.tabBtn, tab === "glossary" && styles.tabBtnActive]}
        >
          <Text style={[styles.tabText, tab === "glossary" && styles.tabTextActive]}>
            {t?.("What's the difference?") || "What's the difference?"}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity onPress={closeAll} style={styles.closeBtn}>
          <MaterialCommunityIcons name="close" size={20} color={styles._c.text} />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderNewTicket = () => (
    <View style={styles.panel}>
      {renderError()}

      {!!subLine && (
        <View style={styles.subLineBox}>
          <Text style={styles.subLineText}>{subLine}</Text>
        </View>
      )}

      <Text style={styles.label}>{t?.("Subject") || "Subject"}</Text>
      <TextInput
        value={subject}
        onChangeText={setSubject}
        placeholder={t?.("Short summary") || "Short summary"}
        placeholderTextColor={styles._c.placeholder}
        style={styles.input}
      />

      <View style={styles.row}>
        <View style={{ flex: 1 }}>
          <Text style={styles.label}>{t?.("Category") || "Category"}</Text>
          <TextInput
            value={category}
            onChangeText={setCategory}
            placeholder="general / login / content / bug"
            placeholderTextColor={styles._c.placeholder}
            style={styles.input}
          />
        </View>

        <View style={{ width: 12 }} />

        <View style={{ width: 140 }}>
          <Text style={styles.label}>{t?.("Priority") || "Priority"}</Text>
          <TextInput
            value={priority}
            onChangeText={setPriority}
            placeholder="normal"
            placeholderTextColor={styles._c.placeholder}
            style={styles.input}
          />
        </View>
      </View>

      <Text style={styles.label}>{t?.("Message") || "Message"}</Text>
      <TextInput
        value={message}
        onChangeText={setMessage}
        placeholder={t?.("Describe the issue or question") || "Describe the issue or question"}
        placeholderTextColor={styles._c.placeholder}
        style={[styles.input, styles.multiline]}
        multiline
      />

      <TouchableOpacity
        onPress={submitTicket}
        disabled={createBusy}
        style={[styles.primaryBtn, createBusy && { opacity: 0.6 }]}
      >
        {createBusy ? (
          <ActivityIndicator />
        ) : (
          <>
            <MaterialCommunityIcons name="send" size={18} color="#fff" />
            <Text style={styles.primaryBtnText}>{t?.("Submit Ticket") || "Submit Ticket"}</Text>
          </>
        )}
      </TouchableOpacity>
    </View>
  );

  const renderTicketsList = () => (
    <View style={styles.panel}>
      {renderError()}

      <View style={styles.listHeader}>
        <Text style={styles.sectionTitle}>
          {isInternalAdmin ? (t?.("All Tickets") || "All Tickets") : (t?.("My Tickets") || "My Tickets")}
        </Text>

        <TouchableOpacity onPress={fetchTickets} style={styles.smallBtn}>
          <MaterialCommunityIcons name="refresh" size={16} color={styles._c.text} />
          <Text style={styles.smallBtnText}>{t?.("Refresh") || "Refresh"}</Text>
        </TouchableOpacity>
      </View>

      {ticketsBusy ? (
        <View style={{ padding: 16 }}>
          <ActivityIndicator />
        </View>
      ) : tickets.length === 0 ? (
        <View style={{ padding: 16 }}>
          <Text style={{ color: styles._c.muted }}>
            {t?.("No tickets yet. Create one from New Ticket.") || "No tickets yet. Create one from New Ticket."}
          </Text>
        </View>
      ) : (
        <ScrollView style={styles.scroll}>
          {tickets.map((tk) => (
            <TouchableOpacity key={tk.id} onPress={() => openTicket(tk)} style={styles.ticketRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.ticketSubject} numberOfLines={1}>
                  {tk.subject}
                </Text>
                <Text style={styles.ticketMeta}>
                  #{tk.id} • {statusLabel(tk.status)} • {fmtDateTime(tk.updated_at)}
                </Text>
              </View>
              <MaterialCommunityIcons name="chevron-right" size={20} color={styles._c.muted} />
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}
    </View>
  );

  const renderTicketDetail = () => (
    <View style={styles.panel}>
      {renderError()}

      <View style={styles.detailHeader}>
        <TouchableOpacity onPress={() => setTab("tickets")} style={styles.smallBtn}>
          <MaterialCommunityIcons name="chevron-left" size={18} color={styles._c.text} />
          <Text style={styles.smallBtnText}>{t?.("Back") || "Back"}</Text>
        </TouchableOpacity>

        <View style={{ flex: 1, marginLeft: 8 }}>
          <Text style={styles.sectionTitle} numberOfLines={1}>
            {selectedTicket?.subject || (t?.("Ticket") || "Ticket")}
          </Text>
          <Text style={styles.ticketMeta}>
            #{selectedTicket?.id} • {statusLabel(selectedTicket?.status)}
          </Text>
        </View>

        {canReopen && (
          <TouchableOpacity onPress={reopenTicket} style={styles.warnBtn}>
            <MaterialCommunityIcons name="backup-restore" size={16} color="#fff" />
            <Text style={styles.warnBtnText}>{t?.("Reopen") || "Reopen"}</Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity onPress={() => fetchTicketDetail(selectedTicket?.id)} style={styles.smallBtn}>
          <MaterialCommunityIcons name="refresh" size={16} color={styles._c.text} />
          <Text style={styles.smallBtnText}>{t?.("Reload") || "Reload"}</Text>
        </TouchableOpacity>
      </View>

      {/* Internal admin controls */}
      {isInternalAdmin && selectedTicket?.id ? (
        <View style={styles.adminControls}>
          <Text style={styles.adminLabel}>Status:</Text>
          <View style={styles.statusRow}>
            {STATUS_ORDER.map((s) => (
              <TouchableOpacity
                key={s}
                onPress={() => updateStatus(s)}
                style={[
                  styles.statusBtn,
                  statusLabel(selectedTicket?.status) === s && styles.statusBtnActive,
                ]}
              >
                <Text
                  style={[
                    styles.statusBtnText,
                    statusLabel(selectedTicket?.status) === s && styles.statusBtnTextActive,
                  ]}
                >
                  {s}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.assignRow}>
            <Text style={styles.adminLabel}>Assign to:</Text>
            <TextInput
              value={assignTo}
              onChangeText={setAssignTo}
              placeholder="email@..."
              placeholderTextColor={styles._c.placeholder}
              style={[styles.input, { flex: 1, marginBottom: 0 }]}
            />
            <TouchableOpacity onPress={updateAssignment} style={styles.smallBtn}>
              <MaterialCommunityIcons name="content-save-outline" size={16} color={styles._c.text} />
              <Text style={styles.smallBtnText}>Save</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : null}

      {detailBusy ? (
        <View style={{ padding: 16 }}>
          <ActivityIndicator />
        </View>
      ) : (
        <>
          <ScrollView ref={scrollRef} style={styles.scroll}>
            {(messages || []).map((m) => (
              <View
                key={m.id}
                style={[
                  styles.msgBubble,
                  norm(m.author_email) === norm(user?.email) ? styles.msgMine : styles.msgOther,
                  m.is_internal ? styles.msgInternal : null,
                ]}
              >
                <Text style={styles.msgAuthor}>
                  {m.is_internal ? "🔒 " : ""}{m.author_email}
                </Text>
                <Text style={styles.msgText}>{m.message}</Text>
                <Text style={styles.msgTime}>{fmtDateTime(m.created_at)}</Text>
              </View>
            ))}
          </ScrollView>

          <View style={styles.replyBox}>
            {isInternalAdmin && (
              <TouchableOpacity
                onPress={() => setReplyInternal((v) => !v)}
                style={styles.checkbox}
              >
                <MaterialCommunityIcons
                  name={replyInternal ? "checkbox-marked" : "checkbox-blank-outline"}
                  size={18}
                  color={replyInternal ? "#22c55e" : styles._c.muted}
                />
                <Text style={styles.checkboxText}>{t?.("Internal note") || "Internal note"}</Text>
              </TouchableOpacity>
            )}

            <View style={styles.replyRow}>
              <TextInput
                value={replyText}
                onChangeText={setReplyText}
                placeholder={t?.("Write a reply…") || "Write a reply…"}
                placeholderTextColor={styles._c.placeholder}
                style={[styles.input, { flex: 1, marginBottom: 0 }]}
                multiline
              />
              <TouchableOpacity
                onPress={sendReply}
                disabled={replyBusy || !replyText.trim()}
                style={[styles.sendBtn, (replyBusy || !replyText.trim()) && { opacity: 0.5 }]}
              >
                {replyBusy ? <ActivityIndicator /> : <MaterialCommunityIcons name="send" size={18} color="#fff" />}
              </TouchableOpacity>
            </View>

            {/* Small policy note for non-internal users */}
            {!isInternalAdmin && (isSchoolAdmin || norm(user?.role) === "teacher" || norm(user?.role) === "student") && (
              <Text style={styles.policyNote}>
                {t?.("Tip: If this ticket is marked resolved, you can reopen it if the issue persists.") ||
                  "Tip: If this ticket is marked resolved, you can reopen it if the issue persists."}
              </Text>
            )}
          </View>
        </>
      )}
    </View>
  );

  // AIT-251: glossary panel — explains Quiz / Test / Homework Help
  const renderGlossary = () => (
    <ScrollView style={styles.panel} contentContainerStyle={{ paddingBottom: 12 }}>
      <Text style={[styles.sectionTitle, { marginBottom: 12 }]}>
        {t?.("What's the difference?") || "What's the difference?"}
      </Text>
      {GLOSSARY_KINDS.map((kind) => {
        const entry = getGlossaryEntry(kind);
        if (!entry) return null;
        const accent = getGlossaryAccentColor(entry.accent, isDarkMode) || styles._c.primary;
        return (
          <View
            key={kind}
            style={[
              styles.glossaryRow,
              { borderColor: styles._c.border, backgroundColor: isDarkMode ? "#0b1220" : "#f9fafb" },
            ]}
          >
            <View style={[styles.glossaryIconChip, { backgroundColor: `${accent}22`, borderColor: accent }]}>
              <MaterialCommunityIcons name={entry.iconName} size={22} color={accent} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[styles.glossaryLabel, { color: styles._c.text }]}>
                {t?.(entry.labelKey) || entry.labelKey}
              </Text>
              <Text style={[styles.glossaryDefinition, { color: styles._c.muted }]}>
                {t?.(entry.definitionKey) || entry.definitionKey}
              </Text>
            </View>
          </View>
        );
      })}
    </ScrollView>
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={closeAll}>
      <View style={styles.backdrop}>
        <View style={styles.modalCard}>
          {renderTopBar()}
          {tab === "new" && renderNewTicket()}
          {tab === "tickets" && renderTicketsList()}
          {tab === "detail" && renderTicketDetail()}
          {tab === "glossary" && renderGlossary()}
        </View>
      </View>
    </Modal>
  );
}

function makeStyles(isDark) {
  const c = {
    bg: isDark ? "#0b1220" : "#f3f4f6",
    card: isDark ? "#0f172a" : "#ffffff",
    border: isDark ? "#22324a" : "#e5e7eb",
    text: isDark ? "#e5e7eb" : "#111827",
    muted: isDark ? "#9ca3af" : "#6b7280",
    placeholder: isDark ? "#64748b" : "#9ca3af",
    errorBg: isDark ? "#3b0d0d" : "#fee2e2",
    errorText: isDark ? "#fecaca" : "#991b1b",
    primary: "#2563eb",
    warn: "#f59e0b",
  };

  const s = StyleSheet.create({
    _c: c,
    backdrop: {
      flex: 1,
      backgroundColor: "rgba(0,0,0,0.55)",
      justifyContent: "center",
      padding: 16,
    },
    modalCard: {
      backgroundColor: c.card,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: c.border,
      overflow: "hidden",
      maxHeight: "90%",
    },

    topBar: {
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: c.bg,
    },
    topTitle: { color: c.text, fontWeight: "700", marginLeft: 8, fontSize: 16 },

    tabBtn: {
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: c.border,
    },
    tabBtnActive: {
      backgroundColor: "rgba(37,99,235,0.18)",
      borderColor: "rgba(37,99,235,0.45)",
    },
    tabText: { color: c.muted, fontSize: 13, fontWeight: "600" },
    tabTextActive: { color: c.text },

    closeBtn: {
      width: 34,
      height: 34,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: c.border,
      alignItems: "center",
      justifyContent: "center",
      marginLeft: 6,
    },

    panel: { padding: 12 },

    subLineBox: {
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: "rgba(37,99,235,0.10)",
      padding: 10,
      borderRadius: 10,
      marginBottom: 10,
    },
    subLineText: { color: c.text, fontWeight: "800", fontSize: 12 },

    label: {
      color: c.muted,
      fontSize: 12,
      fontWeight: "700",
      marginBottom: 6,
      marginTop: 10,
    },
    input: {
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: isDark ? "#0b1220" : "#f9fafb",
      color: c.text,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderRadius: 10,
      marginBottom: 10,
      fontSize: 14,
    },
    multiline: { minHeight: 110, textAlignVertical: "top" },

    row: { flexDirection: "row", alignItems: "center" },

    primaryBtn: {
      marginTop: 8,
      backgroundColor: c.primary,
      paddingVertical: 12,
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "row",
      gap: 8,
    },
    primaryBtnText: { color: "#fff", fontWeight: "800" },

    errorBox: {
      backgroundColor: c.errorBg,
      borderColor: c.border,
      borderWidth: 1,
      padding: 10,
      borderRadius: 10,
      marginBottom: 10,
    },
    errorText: { color: c.errorText, fontWeight: "700" },

    listHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 8,
    },
    sectionTitle: { color: c.text, fontWeight: "800", fontSize: 16 },

    smallBtn: {
      flexDirection: "row",
      gap: 6,
      alignItems: "center",
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: c.border,
    },
    smallBtnText: { color: c.text, fontWeight: "700", fontSize: 12 },

    warnBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      paddingHorizontal: 10,
      paddingVertical: 8,
      borderRadius: 10,
      backgroundColor: c.warn,
      marginRight: 8,
    },
    warnBtnText: { color: "#fff", fontWeight: "900", fontSize: 12 },

    scroll: {
      borderWidth: 1,
      borderColor: c.border,
      borderRadius: 12,
      overflow: "hidden",
      maxHeight: 420,
    },

    ticketRow: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    ticketSubject: { color: c.text, fontWeight: "800" },
    ticketMeta: { color: c.muted, marginTop: 2, fontSize: 12 },

    detailHeader: { flexDirection: "row", alignItems: "center", marginBottom: 10 },

    adminControls: {
      borderWidth: 1,
      borderColor: c.border,
      borderRadius: 12,
      padding: 10,
      marginBottom: 10,
      backgroundColor: isDark ? "#0b1220" : "#f9fafb",
    },
    adminLabel: { color: c.muted, fontWeight: "900", marginBottom: 6 },

    statusRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
    statusBtn: {
      borderWidth: 1,
      borderColor: c.border,
      borderRadius: 999,
      paddingHorizontal: 10,
      paddingVertical: 6,
    },
    statusBtnActive: {
      backgroundColor: "rgba(37,99,235,0.18)",
      borderColor: "rgba(37,99,235,0.45)",
    },
    statusBtnText: { color: c.muted, fontWeight: "900", fontSize: 12 },
    statusBtnTextActive: { color: c.text },

    assignRow: { flexDirection: "row", alignItems: "center", gap: 8, marginTop: 10 },

    msgBubble: {
      margin: 8,
      padding: 10,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: c.border,
      maxWidth: "92%",
    },
    msgMine: { alignSelf: "flex-end", backgroundColor: "rgba(37,99,235,0.12)" },
    msgOther: { alignSelf: "flex-start", backgroundColor: isDark ? "#0b1220" : "#ffffff" },
    msgInternal: { borderColor: "rgba(245,158,11,0.6)" },
    msgAuthor: { color: c.muted, fontSize: 12, fontWeight: "700" },
    msgText: { color: c.text, marginTop: 4, fontSize: 14 },
    msgTime: { color: c.muted, marginTop: 6, fontSize: 11 },

    replyBox: { borderTopWidth: 1, borderTopColor: c.border, paddingTop: 10, marginTop: 8 },
    replyRow: { flexDirection: "row", alignItems: "flex-end", gap: 10 },

    sendBtn: {
      backgroundColor: c.primary,
      width: 44,
      height: 44,
      borderRadius: 12,
      alignItems: "center",
      justifyContent: "center",
    },

    checkbox: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 8, opacity: 0.95 },
    checkboxText: { color: c.text, fontWeight: "700" },

    policyNote: { marginTop: 8, color: c.muted, fontSize: 12, fontWeight: "700" },

    /* AIT-251: glossary panel rows */
    glossaryRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 12,
      padding: 12,
      borderRadius: 12,
      borderWidth: 1,
      marginBottom: 10,
    },
    glossaryIconChip: {
      width: 40,
      height: 40,
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
    },
    glossaryLabel: {
      fontWeight: "800",
      fontSize: 15,
      marginBottom: 4,
    },
    glossaryDefinition: {
      fontSize: 13,
      lineHeight: 18,
    },
  });

  s._c = c;
  return s;
}


// // frontend/app/components/SupportCenterModal.js
// // # BDD 01/21/2026 - add_subscription_notifications_and_support

// import React, { useEffect, useMemo, useRef, useState } from "react";
// import {
//   Modal,
//   View,
//   Text,
//   TouchableOpacity,
//   ScrollView,
//   TextInput,
//   ActivityIndicator,
//   StyleSheet,
//   Platform,
// } from "react-native";
// import { MaterialCommunityIcons } from "@expo/vector-icons";
// import axios from "axios";

// /**
//  * SupportCenterModal
//  *
//  * Backend endpoints used (your FastAPI):
//  * - POST   /api/support/tickets
//  * - GET    /api/support/tickets
//  * - GET    /api/support/tickets/{id}
//  * - POST   /api/support/tickets/{id}/messages
//  *
//  * IMPORTANT:
//  * Your app currently uses axios.get("profile") style (relative).
//  * This helper makes it work whether axios.baseURL already includes "/api" or not.
//  */
// function apiPath(path) {
//   const p = String(path || "").replace(/^\/+/, ""); // remove leading /
//   const base = String(axios?.defaults?.baseURL || "");
//   // If baseURL already contains "/api", do NOT prefix "api/"
//   if (base.includes("/api")) return p;
//   return `api/${p}`;
// }

// const fmtDateTime = (iso) => {
//   if (!iso) return "";
//   try {
//     const d = new Date(iso);
//     return d.toLocaleString();
//   } catch {
//     return String(iso);
//   }
// };

// export default function SupportCenterModal({ visible, onClose, user, isDarkMode, t }) {
//   const styles = useMemo(() => makeStyles(isDarkMode), [isDarkMode]);
//   const isInternalAdmin = !!user?.is_internal_admin;

//   // tabs: "new" | "tickets" | "detail"
//   const [tab, setTab] = useState("tickets");

//   // Create ticket
//   const [subject, setSubject] = useState("");
//   const [category, setCategory] = useState("general");
//   const [priority, setPriority] = useState("normal");
//   const [message, setMessage] = useState("");
//   const [createBusy, setCreateBusy] = useState(false);

//   // Tickets list
//   const [ticketsBusy, setTicketsBusy] = useState(false);
//   const [tickets, setTickets] = useState([]);
//   const [ticketsErr, setTicketsErr] = useState("");

//   // Ticket detail
//   const [selectedTicket, setSelectedTicket] = useState(null);
//   const [detailBusy, setDetailBusy] = useState(false);
//   const [messages, setMessages] = useState([]);
//   const [replyText, setReplyText] = useState("");
//   const [replyBusy, setReplyBusy] = useState(false);
//   const [replyInternal, setReplyInternal] = useState(false);

//   const scrollRef = useRef(null);

//   // Reset / load when opened
//   useEffect(() => {
//     if (!visible) return;

//     setTicketsErr("");
//     setReplyInternal(false);

//     // default tab
//     setTab("tickets");

//     // load tickets immediately
//     fetchTickets();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [visible]);

//   const fetchTickets = async () => {
//     try {
//       setTicketsBusy(true);
//       setTicketsErr("");
//       const resp = await axios.get(apiPath("support/tickets"));
//       const list = resp?.data?.tickets || [];
//       setTickets(list);
//     } catch (e) {
//       setTicketsErr(e?.response?.data?.detail || String(e));
//     } finally {
//       setTicketsBusy(false);
//     }
//   };

//   const openTicket = async (ticket) => {
//     if (!ticket?.id) return;
//     setSelectedTicket(ticket);
//     setTab("detail");
//     await fetchTicketDetail(ticket.id);
//   };

//   const fetchTicketDetail = async (ticketId) => {
//     try {
//       setDetailBusy(true);
//       const resp = await axios.get(apiPath(`support/tickets/${ticketId}`));
//       setSelectedTicket(resp?.data?.ticket || selectedTicket);
//       setMessages(resp?.data?.messages || []);
//       // scroll to bottom
//       setTimeout(() => scrollRef.current?.scrollToEnd?.({ animated: true }), 300);
//     } catch (e) {
//       setTicketsErr(e?.response?.data?.detail || String(e));
//     } finally {
//       setDetailBusy(false);
//     }
//   };

//   const submitTicket = async () => {
//     const sub = subject.trim();
//     const msg = message.trim();
//     if (!sub || !msg) {
//       setTicketsErr("Please enter a subject and message.");
//       return;
//     }

//     try {
//       setCreateBusy(true);
//       setTicketsErr("");

//       const payload = {
//         subject: sub,
//         category: (category || "general").trim(),
//         priority: (priority || "normal").trim(),
//         message: msg,
//         metadata: {
//           platform: Platform.OS,
//           user_role: user?.role,
//           school_id: user?.school_id ?? null,
//         },
//       };

//       const resp = await axios.post(apiPath("support/tickets"), payload);
//       const newId = resp?.data?.ticket_id;

//       // Clear form
//       setSubject("");
//       setMessage("");
//       setCategory("general");
//       setPriority("normal");

//       // Refresh tickets
//       await fetchTickets();

//       // Auto-open the newly created ticket if we have id
//       if (newId) {
//         await openTicket({ id: newId });
//       } else {
//         setTab("tickets");
//       }
//     } catch (e) {
//       setTicketsErr(e?.response?.data?.detail || String(e));
//     } finally {
//       setCreateBusy(false);
//     }
//   };

//   const sendReply = async () => {
//     const txt = replyText.trim();
//     if (!txt || !selectedTicket?.id) return;

//     try {
//       setReplyBusy(true);
//       setTicketsErr("");

//       const payload = {
//         message: txt,
//         is_internal: !!replyInternal,
//       };

//       await axios.post(apiPath(`support/tickets/${selectedTicket.id}/messages`), payload);

//       setReplyText("");
//       // refresh thread
//       await fetchTicketDetail(selectedTicket.id);
//     } catch (e) {
//       setTicketsErr(e?.response?.data?.detail || String(e));
//     } finally {
//       setReplyBusy(false);
//     }
//   };

//   const closeAll = () => {
//     // keep state simple; just close
//     onClose?.();
//   };

//   const renderTopBar = () => (
//     <View style={styles.topBar}>
//       <View style={{ flexDirection: "row", alignItems: "center" }}>
//         <MaterialCommunityIcons name="lifebuoy" size={20} color={styles._c.text} />
//         <Text style={styles.topTitle}>{t?.("Support") || "Support"}</Text>
//       </View>

//       <View style={{ flexDirection: "row", gap: 10, alignItems: "center" }}>
//         {/* Tabs */}
//         <TouchableOpacity
//           onPress={() => setTab("new")}
//           style={[styles.tabBtn, tab === "new" && styles.tabBtnActive]}
//         >
//           <Text style={[styles.tabText, tab === "new" && styles.tabTextActive]}>
//             {t?.("New Ticket") || "New Ticket"}
//           </Text>
//         </TouchableOpacity>

//         <TouchableOpacity
//           onPress={() => { setTab("tickets"); fetchTickets(); }}
//           style={[styles.tabBtn, tab === "tickets" && styles.tabBtnActive]}
//         >
//           <Text style={[styles.tabText, tab === "tickets" && styles.tabTextActive]}>
//             {isInternalAdmin ? (t?.("Tickets") || "Tickets") : (t?.("My Tickets") || "My Tickets")}
//           </Text>
//         </TouchableOpacity>

//         <TouchableOpacity onPress={closeAll} style={styles.closeBtn}>
//           <MaterialCommunityIcons name="close" size={20} color={styles._c.text} />
//         </TouchableOpacity>
//       </View>
//     </View>
//   );

//   const renderError = () =>
//     ticketsErr ? (
//       <View style={styles.errorBox}>
//         <Text style={styles.errorText}>{ticketsErr}</Text>
//       </View>
//     ) : null;

//   const renderNewTicket = () => (
//     <View style={styles.panel}>
//       {renderError()}

//       <Text style={styles.label}>{t?.("Subject") || "Subject"}</Text>
//       <TextInput
//         value={subject}
//         onChangeText={setSubject}
//         placeholder={t?.("Short summary") || "Short summary"}
//         placeholderTextColor={styles._c.placeholder}
//         style={styles.input}
//       />

//       <View style={styles.row}>
//         <View style={{ flex: 1 }}>
//           <Text style={styles.label}>{t?.("Category") || "Category"}</Text>
//           <TextInput
//             value={category}
//             onChangeText={setCategory}
//             placeholder="general / login / content / bug"
//             placeholderTextColor={styles._c.placeholder}
//             style={styles.input}
//           />
//         </View>

//         <View style={{ width: 12 }} />

//         <View style={{ width: 140 }}>
//           <Text style={styles.label}>{t?.("Priority") || "Priority"}</Text>
//           <TextInput
//             value={priority}
//             onChangeText={setPriority}
//             placeholder="normal"
//             placeholderTextColor={styles._c.placeholder}
//             style={styles.input}
//           />
//         </View>
//       </View>

//       <Text style={styles.label}>{t?.("Message") || "Message"}</Text>
//       <TextInput
//         value={message}
//         onChangeText={setMessage}
//         placeholder={t?.("Describe the issue or question") || "Describe the issue or question"}
//         placeholderTextColor={styles._c.placeholder}
//         style={[styles.input, styles.multiline]}
//         multiline
//       />

//       <TouchableOpacity
//         onPress={submitTicket}
//         disabled={createBusy}
//         style={[styles.primaryBtn, createBusy && { opacity: 0.6 }]}
//       >
//         {createBusy ? (
//           <ActivityIndicator />
//         ) : (
//           <>
//             <MaterialCommunityIcons name="send" size={18} color="#fff" />
//             <Text style={styles.primaryBtnText}>{t?.("Submit Ticket") || "Submit Ticket"}</Text>
//           </>
//         )}
//       </TouchableOpacity>
//     </View>
//   );

//   const renderTicketsList = () => (
//     <View style={styles.panel}>
//       {renderError()}

//       <View style={styles.listHeader}>
//         <Text style={styles.sectionTitle}>
//           {isInternalAdmin ? (t?.("All Tickets") || "All Tickets") : (t?.("My Tickets") || "My Tickets")}
//         </Text>

//         <TouchableOpacity onPress={fetchTickets} style={styles.smallBtn}>
//           <MaterialCommunityIcons name="refresh" size={16} color={styles._c.text} />
//           <Text style={styles.smallBtnText}>{t?.("Refresh") || "Refresh"}</Text>
//         </TouchableOpacity>
//       </View>

//       {ticketsBusy ? (
//         <View style={{ padding: 16 }}>
//           <ActivityIndicator />
//         </View>
//       ) : tickets.length === 0 ? (
//         <View style={{ padding: 16 }}>
//           <Text style={{ color: styles._c.muted }}>
//             {t?.("No tickets yet. Create one from New Ticket.") || "No tickets yet. Create one from New Ticket."}
//           </Text>
//         </View>
//       ) : (
//         <ScrollView style={styles.scroll}>
//           {tickets.map((tk) => (
//             <TouchableOpacity key={tk.id} onPress={() => openTicket(tk)} style={styles.ticketRow}>
//               <View style={{ flex: 1 }}>
//                 <Text style={styles.ticketSubject} numberOfLines={1}>{tk.subject}</Text>
//                 <Text style={styles.ticketMeta}>
//                   #{tk.id} • {tk.status} • {fmtDateTime(tk.updated_at)}
//                 </Text>
//               </View>
//               <MaterialCommunityIcons name="chevron-right" size={20} color={styles._c.muted} />
//             </TouchableOpacity>
//           ))}
//         </ScrollView>
//       )}
//     </View>
//   );

//   const renderTicketDetail = () => (
//     <View style={styles.panel}>
//       {renderError()}

//       <View style={styles.detailHeader}>
//         <TouchableOpacity onPress={() => setTab("tickets")} style={styles.smallBtn}>
//           <MaterialCommunityIcons name="chevron-left" size={18} color={styles._c.text} />
//           <Text style={styles.smallBtnText}>{t?.("Back") || "Back"}</Text>
//         </TouchableOpacity>

//         <View style={{ flex: 1, marginLeft: 8 }}>
//           <Text style={styles.sectionTitle} numberOfLines={1}>
//             {selectedTicket?.subject || (t?.("Ticket") || "Ticket")}
//           </Text>
//           <Text style={styles.ticketMeta}>
//             #{selectedTicket?.id} • {selectedTicket?.status}
//           </Text>
//         </View>

//         <TouchableOpacity onPress={() => fetchTicketDetail(selectedTicket?.id)} style={styles.smallBtn}>
//           <MaterialCommunityIcons name="refresh" size={16} color={styles._c.text} />
//           <Text style={styles.smallBtnText}>{t?.("Reload") || "Reload"}</Text>
//         </TouchableOpacity>
//       </View>

//       {detailBusy ? (
//         <View style={{ padding: 16 }}>
//           <ActivityIndicator />
//         </View>
//       ) : (
//         <>
//           <ScrollView ref={scrollRef} style={styles.scroll}>
//             {(messages || []).map((m) => (
//               <View
//                 key={m.id}
//                 style={[
//                   styles.msgBubble,
//                   (m.author_email || "").toLowerCase() === (user?.email || "").toLowerCase()
//                     ? styles.msgMine
//                     : styles.msgOther,
//                   m.is_internal ? styles.msgInternal : null,
//                 ]}
//               >
//                 <Text style={styles.msgAuthor}>
//                   {m.is_internal ? "🔒 " : ""}{m.author_email}
//                 </Text>
//                 <Text style={styles.msgText}>{m.message}</Text>
//                 <Text style={styles.msgTime}>{fmtDateTime(m.created_at)}</Text>
//               </View>
//             ))}
//           </ScrollView>

//           <View style={styles.replyBox}>
//             {isInternalAdmin && (
//               <TouchableOpacity
//                 onPress={() => setReplyInternal((v) => !v)}
//                 style={[styles.checkbox, replyInternal && styles.checkboxOn]}
//               >
//                 <MaterialCommunityIcons
//                   name={replyInternal ? "checkbox-marked" : "checkbox-blank-outline"}
//                   size={18}
//                   color={replyInternal ? "#22c55e" : styles._c.muted}
//                 />
//                 <Text style={styles.checkboxText}>
//                   {t?.("Internal note") || "Internal note"}
//                 </Text>
//               </TouchableOpacity>
//             )}

//             <View style={styles.replyRow}>
//               <TextInput
//                 value={replyText}
//                 onChangeText={setReplyText}
//                 placeholder={t?.("Write a reply…") || "Write a reply…"}
//                 placeholderTextColor={styles._c.placeholder}
//                 style={[styles.input, { flex: 1, marginBottom: 0 }]}
//                 multiline
//               />
//               <TouchableOpacity
//                 onPress={sendReply}
//                 disabled={replyBusy || !replyText.trim()}
//                 style={[styles.sendBtn, (replyBusy || !replyText.trim()) && { opacity: 0.5 }]}
//               >
//                 {replyBusy ? <ActivityIndicator /> : <MaterialCommunityIcons name="send" size={18} color="#fff" />}
//               </TouchableOpacity>
//             </View>
//           </View>
//         </>
//       )}
//     </View>
//   );

//   return (
//     <Modal visible={visible} transparent animationType="fade" onRequestClose={closeAll}>
//       <View style={styles.backdrop}>
//         <View style={styles.modalCard}>
//           {renderTopBar()}

//           {tab === "new" && renderNewTicket()}
//           {tab === "tickets" && renderTicketsList()}
//           {tab === "detail" && renderTicketDetail()}
//         </View>
//       </View>
//     </Modal>
//   );
// }

// function makeStyles(isDark) {
//   const c = {
//     bg: isDark ? "#0b1220" : "#f3f4f6",
//     card: isDark ? "#0f172a" : "#ffffff",
//     border: isDark ? "#22324a" : "#e5e7eb",
//     text: isDark ? "#e5e7eb" : "#111827",
//     muted: isDark ? "#9ca3af" : "#6b7280",
//     placeholder: isDark ? "#64748b" : "#9ca3af",
//     errorBg: isDark ? "#3b0d0d" : "#fee2e2",
//     errorText: isDark ? "#fecaca" : "#991b1b",
//     primary: "#2563eb",
//   };

//   const s = StyleSheet.create({
//     _c: c,

//     backdrop: {
//       flex: 1,
//       backgroundColor: "rgba(0,0,0,0.55)",
//       justifyContent: "center",
//       padding: 16,
//     },
//     modalCard: {
//       backgroundColor: c.card,
//       borderRadius: 12,
//       borderWidth: 1,
//       borderColor: c.border,
//       overflow: "hidden",
//       maxHeight: "90%",
//     },

//     topBar: {
//       paddingHorizontal: 12,
//       paddingVertical: 10,
//       borderBottomWidth: 1,
//       borderBottomColor: c.border,
//       flexDirection: "row",
//       alignItems: "center",
//       justifyContent: "space-between",
//       backgroundColor: c.bg,
//     },
//     topTitle: { color: c.text, fontWeight: "700", marginLeft: 8, fontSize: 16 },

//     tabBtn: {
//       paddingHorizontal: 10,
//       paddingVertical: 6,
//       borderRadius: 8,
//       borderWidth: 1,
//       borderColor: c.border,
//       backgroundColor: "transparent",
//     },
//     tabBtnActive: {
//       backgroundColor: "rgba(37,99,235,0.18)",
//       borderColor: "rgba(37,99,235,0.45)",
//     },
//     tabText: { color: c.muted, fontSize: 13, fontWeight: "600" },
//     tabTextActive: { color: c.text },

//     closeBtn: {
//       width: 34,
//       height: 34,
//       borderRadius: 10,
//       borderWidth: 1,
//       borderColor: c.border,
//       alignItems: "center",
//       justifyContent: "center",
//       backgroundColor: "transparent",
//       marginLeft: 6,
//     },

//     panel: {
//       padding: 12,
//     },

//     label: {
//       color: c.muted,
//       fontSize: 12,
//       fontWeight: "700",
//       marginBottom: 6,
//       marginTop: 10,
//     },
//     input: {
//       borderWidth: 1,
//       borderColor: c.border,
//       backgroundColor: isDark ? "#0b1220" : "#f9fafb",
//       color: c.text,
//       paddingHorizontal: 12,
//       paddingVertical: 10,
//       borderRadius: 10,
//       marginBottom: 10,
//       fontSize: 14,
//     },
//     multiline: {
//       minHeight: 110,
//       textAlignVertical: "top",
//     },

//     row: { flexDirection: "row", alignItems: "center" },

//     primaryBtn: {
//       marginTop: 8,
//       backgroundColor: c.primary,
//       paddingVertical: 12,
//       borderRadius: 10,
//       alignItems: "center",
//       justifyContent: "center",
//       flexDirection: "row",
//       gap: 8,
//     },
//     primaryBtnText: { color: "#fff", fontWeight: "800" },

//     errorBox: {
//       backgroundColor: c.errorBg,
//       borderColor: c.border,
//       borderWidth: 1,
//       padding: 10,
//       borderRadius: 10,
//       marginBottom: 10,
//     },
//     errorText: { color: c.errorText, fontWeight: "700" },

//     listHeader: {
//       flexDirection: "row",
//       alignItems: "center",
//       justifyContent: "space-between",
//       marginBottom: 8,
//     },
//     sectionTitle: { color: c.text, fontWeight: "800", fontSize: 16 },

//     smallBtn: {
//       flexDirection: "row",
//       gap: 6,
//       alignItems: "center",
//       paddingHorizontal: 10,
//       paddingVertical: 6,
//       borderRadius: 10,
//       borderWidth: 1,
//       borderColor: c.border,
//     },
//     smallBtnText: { color: c.text, fontWeight: "700", fontSize: 12 },

//     scroll: {
//       borderWidth: 1,
//       borderColor: c.border,
//       borderRadius: 12,
//       overflow: "hidden",
//       maxHeight: 420,
//     },

//     ticketRow: {
//       padding: 12,
//       borderBottomWidth: 1,
//       borderBottomColor: c.border,
//       flexDirection: "row",
//       alignItems: "center",
//       justifyContent: "space-between",
//       backgroundColor: "transparent",
//     },
//     ticketSubject: { color: c.text, fontWeight: "800" },
//     ticketMeta: { color: c.muted, marginTop: 2, fontSize: 12 },

//     detailHeader: {
//       flexDirection: "row",
//       alignItems: "center",
//       marginBottom: 10,
//     },

//     msgBubble: {
//       margin: 8,
//       padding: 10,
//       borderRadius: 12,
//       borderWidth: 1,
//       borderColor: c.border,
//       maxWidth: "92%",
//     },
//     msgMine: {
//       alignSelf: "flex-end",
//       backgroundColor: "rgba(37,99,235,0.12)",
//     },
//     msgOther: {
//       alignSelf: "flex-start",
//       backgroundColor: isDark ? "#0b1220" : "#f9fafb",
//     },
//     msgInternal: {
//       borderColor: "rgba(245,158,11,0.6)",
//     },
//     msgAuthor: { color: c.muted, fontSize: 12, fontWeight: "700" },
//     msgText: { color: c.text, marginTop: 4, fontSize: 14 },
//     msgTime: { color: c.muted, marginTop: 6, fontSize: 11 },

//     replyBox: {
//       borderTopWidth: 1,
//       borderTopColor: c.border,
//       paddingTop: 10,
//       marginTop: 8,
//     },
//     replyRow: {
//       flexDirection: "row",
//       alignItems: "flex-end",
//       gap: 10,
//     },
//     sendBtn: {
//       backgroundColor: c.primary,
//       width: 44,
//       height: 44,
//       borderRadius: 12,
//       alignItems: "center",
//       justifyContent: "center",
//     },

//     checkbox: {
//       flexDirection: "row",
//       alignItems: "center",
//       gap: 8,
//       marginBottom: 8,
//       opacity: 0.95,
//     },
//     checkboxOn: {},
//     checkboxText: { color: c.text, fontWeight: "700" },
//   });

//   // attach colors for internal use
//   s._c = c;
//   return s;
// }
